// Libs
import moment from 'moment';
import * as yup from 'yup';
import { TFunction } from 'i18next';
// Others
import { cleanObject } from '~/utils/helper/common';
import { IClientContact } from '~/utils/interface/common';
import { DiscountTypeEnum } from '~/utils/enum/common';
import { DateFormatEnum, OrderPickupEnum } from '~/utils/enum/common';
import { IFormQuotes, IQuoteAddOn, IQuoteDetail } from '~/utils/interface/quotes';
import { DEFAULT_LIMIT_PAGE, EMPTY_STRING, REG_EXP } from '~/utils/constants/common';

const buildQuoteAddOnSchema = (t: TFunction): yup.ObjectSchema<IQuoteAddOn> =>
  yup
    .object()
    .shape({
      inventoryId: yup.string().optional().nullable(),
      variantId: yup.string().optional().nullable(),
      variantName: yup.string().optional().nullable(),
      note: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
      laborCost: yup.string().optional().nullable(),
      price: yup.number().optional(),
      discountType: yup.string().optional().nullable(),
      discountRate: yup.string().optional().nullable(),
      availableInventory: yup.string().optional().nullable(),
      duration: yup.number().optional(),
      additionalHours: yup.number().optional(),
      timeLoading: yup.string().optional().nullable(),
      timeUnloading: yup.string().optional().nullable(),
      timeDelivery: yup.string().optional().nullable(),
      timePickup: yup.string().optional().nullable(),
      total: yup.number().optional(),
      isRequired: yup.boolean().optional(),
      isShowPrice: yup.boolean().optional(),
      quantity: yup
        .number()
        .when('isRequired', {
          is: true,
          then: (schema) => schema.moreThan(0, t('common_validate_field_required', { ns: 'common/base' })),
          otherwise: (schema) => schema.min(0).optional(),
        })
        .optional(),
      idRemember: yup
        .string()
        .trim()
        .test(
          'required-when-has-variant',
          t('common_validate_field_required', { ns: 'common/base' }),
          function (value) {
            const { isHasVariant } = this.parent;

            if (isHasVariant && !value) {
              return false;
            }

            return true;
          }
        ),
      isHasVariant: yup.boolean().optional(),
    })
    .optional() as yup.ObjectSchema<IQuoteAddOn>;

export const schema = (t: TFunction): yup.ObjectSchema<IFormQuotes> => {
  return yup.object().shape({
    status: yup.string().trim().optional(),
    clientId: yup
      .string()
      .trim()
      .when('clientContacts[0].email', {
        is: (email: string | undefined) => !email,
        then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
        otherwise: (schema) => schema.optional(),
      }),
    managedBy: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    salesAccountId: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    isConvert: yup.boolean().optional(),
    firstName: yup.string().trim().optional(),
    lastName: yup.string().optional(),
    clientTitle: yup.string().optional(),
    companyName: yup.string().optional(),
    businessType: yup.string().optional(),
    paymentTerms: yup.string().optional(),
    isDamageWaiver: yup.boolean().optional(),
    clientAddress: yup.object().optional(),
    deliveryType: yup.string().optional(),
    listQuoteItems: yup
      .array()
      .of(
        yup.object().shape({
          id: yup
            .string()
            .trim()
            .required(t('common_validate_field_required', { ns: 'common/base' })),
          quantity: yup
            .string()
            .trim()
            .required(t('common_validate_field_required', { ns: 'common/base' })),
          rentalPrice: yup
            .string()
            .trim()
            .required(t('common_validate_field_required', { ns: 'common/base' })),
        })
      )
      .optional(),
    clientContacts: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .trim()
          .test('isValidEmail', t('common_validate_invalid_email', { ns: 'common/base' }), (value) => {
            if (value && !REG_EXP.EMAIL.test(value)) return false;
            return true;
          })
          .optional(),
        phoneNumber: yup
          .string()
          .trim()
          .test('phone-length', t('common_validate_invalid_phone', { ns: 'common/base' }), (value) => {
            if (!value) return true;
            const phoneNumber = value?.replace(REG_EXP.NUMBER, EMPTY_STRING);
            return REG_EXP.PHONE_NUMBER.test(phoneNumber);
          })
          .optional(),
      })
    ),
    additionalContacts: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .trim()
          .test('isValidEmail', t('common_validate_invalid_email', { ns: 'common/base' }), (value) => {
            if (value && !REG_EXP.EMAIL.test(value)) return false;
            return true;
          })
          .optional(),
        phoneNumber: yup
          .string()
          .trim()
          .test('phone-length', t('common_validate_invalid_phone', { ns: 'common/base' }), (value) => {
            if (!value) return true;
            const phoneNumber = value?.replace(REG_EXP.NUMBER, EMPTY_STRING);
            return REG_EXP.PHONE_NUMBER.test(phoneNumber);
          })
          .optional(),
        contactType: yup
          .string()
          .trim()
          .optional()
          .test(
            'duplicate-type',
            t('common_validate_duplicate_relationship', { ns: 'admin/quotes' }),
            function (value) {
              const rootArray = this.from?.[1]?.value?.additionalContacts || [];

              const duplicateCount = rootArray?.filter(
                (item: IClientContact) => item?.contactType?.toLocaleLowerCase() === value?.toLocaleLowerCase()
              ).length;

              return duplicateCount <= 1;
            }
          ),
      })
    ),
    eventTypeId: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    eventTitle: yup.string().trim().optional(),
    startDate: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    startTime: yup.string().trim().optional(),
    endDate: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    endTime: yup.string().trim().optional(),
    // endDate: yup
    //   .string().trim()
    //   .required(t('common_validate_field_required', { ns: 'common/base' }))
    //   .test('validate-date', t('common_validate_compare_date', { ns: 'common/base' }), function (value) {
    //     const { startDate } = this.parent;
    //     if (!startDate || !value) return true;

    //     return moment(value, DateFormatEnum.YYYY_MM_DD).isSameOrAfter(moment(startDate, DateFormatEnum.YYYY_MM_DD));
    //   }),
    // endTime: yup
    //   .string().trim()
    //   .required(t('common_validate_field_required', { ns: 'common/base' }))
    //   .test('validate-time', t('common_validate_compare_time', { ns: 'common/base' }), (value, context) => {
    //     const { startTime } = context.parent;
    //     if (!startTime && !value) return true;

    //     return moment(startTime, DateFormatEnum.HH_mm_A).isBefore(moment(value, DateFormatEnum.HH_mm_A));
    //   }),
    earliestDeliveryDate: yup.string().trim().optional(),
    earliestDeliveryTime: yup.string().trim().optional(),
    deliveryScheduledDate: yup.string().trim().optional(),
    deliveryScheduledTime: yup.string().trim().optional(),
    latestPickupDate: yup.string().trim().optional(),
    latestPickupTime: yup.string().trim().optional(),
    pickupScheduledDate: yup.string().trim().optional(),
    pickupScheduledTime: yup.string().trim().optional(),
    // deliveryAddress: yup
    //   .object({
    //     address: yup.string().trim().required(t('common_validate_field_required', { ns: 'common/base' })),
    //   })
    //   .required(t('common_validate_field_required', { ns: 'common/base' })),
    depositDueDate: yup.string().trim().optional(),
    headCount: yup.string().trim().optional(),
    deliveryAddress: yup.object().when('deliveryOrPickUp', {
      is: OrderPickupEnum.DELIVERY_PICKUP,
      then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
      otherwise: (schema) => schema.optional(),
    }),

    pickupAddress: yup.object().when('deliveryOrPickUp', {
      is: OrderPickupEnum.DELIVERY_PICKUP,
      then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
      otherwise: (schema) => schema.optional(),
    }),
    referralSource: yup.string().trim().optional(),
    repeatBusiness: yup.string().trim().optional(),
    pricingTotal: yup.string().trim().optional(),
    deliveryOrPickUp: yup.string().trim().optional(),
    quoteGroups: yup.array().optional(),
    quoteItems: yup.array().optional(),
    quoteAddOns: yup.array().of(buildQuoteAddOnSchema(t)).optional(),
    productSubtotal: yup.number().optional(),
    laborCost: yup.number().optional(),
    internalNote: yup.string().trim().optional(),
    externalNote: yup.string().trim().optional(),
    deliveryCost: yup.string().trim().optional(),
    damageWaiver: yup.string().trim().optional(),
    laborFee: yup.string().trim().optional(),
    discountType: yup.string().trim().optional(),
    discountRate: yup.string().trim().optional(),
    depositMade: yup.string().trim().optional(),
    poNumber: yup.string().trim().optional(),
    customFees: yup.array().optional(),
    subtotal: yup.string().trim().optional(),
    taxRate: yup.string().trim().optional(),
    taxAmount: yup.string().trim().optional(),
    mileage: yup.string().trim().optional(),
    total: yup.string().trim().optional(),
    isOverbooking: yup.boolean().optional(),
    quoteFile: yup.array().optional(),
    boldSignFile: yup.object().optional(),
    selectGroupQuote: yup.string().trim().optional(),
    selectGroupItem: yup.string().trim().optional(),
    selectIdProductQuote: yup.string().trim().optional(),
    selectIdProductGroup: yup.string().trim().optional(),
    selectIdVariantQuote: yup.string().trim().optional(),
    selectIdVariantGroup: yup.string().trim().optional(),
    selectQuantityProduct: yup.number().optional(),
    deleteProduct: yup.string().trim().optional(),
    deleteChildrenProduct: yup.array().optional(),
    isHasPackage: yup.boolean().optional(),
    deleteFileIds: yup.array().optional(),
    deleteItemIds: yup.array().optional(),
    deleteCustomIds: yup.array().optional(),
    couponId: yup.string().trim().optional(),
    couponAmount: yup.string().trim().optional(),
    depositMadeAmount: yup.string().trim().optional(),
    couponType: yup.string().trim().optional(),
    couponRate: yup.string().trim().optional(),
    templateQuoteId: yup.string().trim().optional(),
    amountDue: yup.string().trim().optional(),
    isTemplate: yup.boolean().optional(),
    quoteItemQuantity: yup.string().trim().optional(),
    listQuoteItemsSelected: yup.array().optional(),
  });
};

export const defaultValuesQuotesForm: IFormQuotes = {
  selectIdVariantQuote: EMPTY_STRING,
  selectIdVariantGroup: EMPTY_STRING,
  deliveryOrPickUp: OrderPickupEnum.DELIVERY_PICKUP,
  pricingTotal: '1',
  taxRate: '0',
  startDate: moment().format(DateFormatEnum.YYYY_MM_DD),
  endDate: moment().add(1, 'day').format(DateFormatEnum.YYYY_MM_DD),
  latestPickupDate: moment().add(4, 'day').format(DateFormatEnum.YYYY_MM_DD),
  earliestDeliveryDate: moment().subtract(3, 'day').format(DateFormatEnum.YYYY_MM_DD),
};

export const convertDataDetailQuotesForm = (dataDetail: IQuoteDetail): IFormQuotes => {
  const { client, quoteCoupon, eventType, fileQuote, quoteCustom, clientAddress, additionalContacts, quoteAddOns } =
    dataDetail;

  const body: IFormQuotes = Object.fromEntries(
    Object.entries({
      status: dataDetail.status,
      eventTitle: dataDetail.eventTitle,
      startDate: dataDetail.startDate,
      startTime: dataDetail.startTime,
      endDate: dataDetail.endDate,
      endTime: dataDetail.endTime,
      deliveryType: dataDetail.deliveryType,
      isDamageWaiver: dataDetail.isDamageWaiver,
      earliestDeliveryDate: dataDetail.earliestDeliveryDate,
      earliestDeliveryTime: dataDetail.earliestDeliveryTime,
      deliveryScheduledDate: dataDetail.deliveryScheduledDate,
      deliveryScheduledTime: dataDetail.deliveryScheduledTime,
      latestPickupDate: dataDetail.latestPickupDate,
      latestPickupTime: dataDetail.latestPickupTime,
      pickupScheduledDate: dataDetail.pickupScheduledDate,
      pickupScheduledTime: dataDetail.pickupScheduledTime,
      deliveryAddress: cleanObject(dataDetail.deliveryAddress, {
        removeKeys: ['id'],
        removeNull: true,
        removeEmptyStrings: true,
      }),
      pickupAddress: cleanObject(dataDetail.pickupAddress, {
        removeKeys: ['id'],
        removeNull: true,
        removeEmptyStrings: true,
      }),
      clientId: client?.clientId,
      firstName: client?.firstName,
      lastName: client?.lastName,
      clientTitle: client?.clientTitle,
      companyName: client?.companyName,
      businessType: client?.businessType,
      clientContacts: client?.clientContacts,
      clientAddress: clientAddress && {
        address: clientAddress.address,
        unitDetail: clientAddress.unitDetail,
        city: clientAddress.city,
        state: clientAddress.state,
        postalCode: clientAddress.postalCode,
        // country: clientAddress.country,
        siteType: clientAddress.siteType,
        lat: clientAddress.lat,
        lng: clientAddress.lng,
      },
      eventTypeId: eventType?.id,
      referralSource: dataDetail.referralSource,
      repeatBusiness: dataDetail.repeatBusiness,
      pricingTotal: dataDetail.pricingTotal,
      deliveryOrPickUp: dataDetail.deliveryOrPickUp,
      productSubtotal: Number(dataDetail.productSubtotal ?? 0),
      laborCost: Number(dataDetail.laborCost ?? 0),
      internalNote: dataDetail.internalNote,
      externalNote: dataDetail.externalNote,
      deliveryCost: dataDetail.deliveryCost || '0',
      damageWaiver: dataDetail.damageWaiver || '0',
      laborFee: dataDetail.laborFee || '0',
      discountType: dataDetail.discountType,
      discountRate: dataDetail.discountRate || '0',
      depositMade: dataDetail.depositMade,
      depositMadeAmount: dataDetail.depositMadeAmount || '0',
      poNumber: dataDetail.poNumber,
      subtotal: dataDetail.subtotal || '0',
      taxRate: dataDetail.taxRate || '0',
      taxAmount: dataDetail.taxAmount || '0',
      mileage: dataDetail.mileage,
      total: dataDetail.total || '0',
      isOverbooking: dataDetail.isOverbooking,
      isHasPackage: dataDetail.isHasPackage,
      couponId: quoteCoupon?.id || undefined,
      couponAmount: quoteCoupon != null ? String(quoteCoupon.couponAmount ?? 0) : '0',
      couponType: quoteCoupon?.discountType ?? EMPTY_STRING,
      couponRate:
        quoteCoupon != null
          ? quoteCoupon.discountType === DiscountTypeEnum.PERCENTAGE
            ? String(quoteCoupon.discountRate ?? 0)
            : String(quoteCoupon.couponAmount ?? 0)
          : '0',
      headCount: dataDetail.headCount,
      depositDueDate: dataDetail.depositDueDate,
      quoteGroups: dataDetail.quoteGroups,
      quoteItems: dataDetail.quoteItems,
      amountDue: dataDetail?.amountDue,
      salesAccountId: dataDetail?.salesPerson?.id,
      managedBy: dataDetail?.managedBy?.id,
      isTemplate: dataDetail?.isTemplate,
      paymentTerms: dataDetail?.paymentTerms,
      quoteItemQuantity: dataDetail?.quoteItems?.length || DEFAULT_LIMIT_PAGE,
    }).filter(([_, value]) => value !== null && value !== undefined && value !== '')
  ) as IFormQuotes;

  body.depositMadeAmount = body.depositMadeAmount || dataDetail.depositMadeAmount || '0';
  body.couponId = body.couponId || undefined;
  body.couponType = body.couponType || undefined;
  body.couponRate = body.couponRate || '0';
  body.couponAmount = body.couponAmount || '0';
  body.deliveryCost = body.deliveryCost || '0';
  body.damageWaiver = body.damageWaiver || '0';
  body.laborFee = body.laborFee || '0';
  body.discountRate = body.discountRate || '0';
  body.subtotal = body.subtotal || '0';
  body.taxRate = body.taxRate || '0';
  body.taxAmount = body.taxAmount || '0';
  body.total = body.total || '0';

  if (fileQuote) {
    body.quoteFile = fileQuote.map((item) => ({
      id: item.id,
      url: item.url,
      file: item as any,
    }));
  }

  if (quoteAddOns && quoteAddOns?.length > 0) {
    body.quoteAddOns = quoteAddOns.map((item) => ({
      ...item,
      isShow: true,
    }));
  }

  if (quoteCustom) {
    body.customFees = quoteCustom.map((item) => ({
      id: String(item?.id ?? EMPTY_STRING),
      customFeeName: String(item?.customFeeName ?? EMPTY_STRING),
      customFeeAmount: String(item?.customFeeAmount ?? EMPTY_STRING),
    }));
  }

  return body;
};
